// frontend/src/pages/reportpage/ReportsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../AdminLayout';
import SaccoFinancialStatePage from '../SaccoFinancialStatePage';
import globalApi from '../../utils/globalFetchWrapper';
import '../ExpensesTableStyles.css';

import { API_URL, monthNames, needsPaymentDetails } from './reportUtils';
import MonthlyBursaryTab from './MonthlyBursaryTab';
import LoansReportTab from './LoansReportTab';
import SharesReportTab from './SharesReportTab';
import ExpensesTab from './ExpensesTab';
import MobileLoanTab from './MobileLoanTab';

const ReportsPage = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('monthly');
  const [monthYear, setMonthYear] = useState('');
  const [monthInput, setMonthInput] = useState('');

  const [monthlyReport, setMonthlyReport] = useState(null);
  const [loansReport, setLoansReport] = useState(null);
  const [loansPage, setLoansPage] = useState(1);
  const [loansPageSize, setLoansPageSize] = useState(50); // 25 | 50 | 100 | 'all'
  const [sharesReport, setSharesReport] = useState(null);
  const [sharesSearch, setSharesSearch] = useState('');

  // EXPENSES
  const [expenseMonthInput, setExpenseMonthInput] = useState('');
  const [expenseYearInput, setExpenseYearInput] = useState('');
  const [expenseFilterMode, setExpenseFilterMode] = useState('month'); // 'month', 'year', or 'all'
  const [expensesReport, setExpensesReport] = useState(null);

  const [expenseForm, setExpenseForm] = useState({
    id: '',
    date: '',
    category: 'Uncategorized',
    description: '',
    amount: '',
    paymentMethod: 'Bank',
    paymentDetails: '',
    paidTo: '',
    reference: '',
    notes: '',
  });

  const [isSavingExpense, setIsSavingExpense] = useState(false);

  // History modal
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExpenseId, setHistoryExpenseId] = useState('');
  const [historyLogs, setHistoryLogs] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const now = new Date();
    const y = now.getFullYear();
    const mIndex = now.getMonth();
    const monthLabel = `${monthNames[mIndex]} ${y}`;
    const monthValue = `${y}-${String(mIndex + 1).padStart(2, '0')}`;

    setMonthYear(monthLabel);
    setMonthInput(monthValue);

    // Expenses default: current month and year; default date: today
    setExpenseMonthInput(monthValue);
    setExpenseYearInput(String(y));
    setExpenseForm((prev) => ({
      ...prev,
      date: now.toISOString().slice(0, 10),
    }));
  }, []);

  const handleMonthChange = (e) => {
    const value = e.target.value;
    setMonthInput(value);

    if (!value) {
      setMonthYear('');
      return;
    }

    const [yearStr, monthStr] = value.split('-');
    const mIndex = parseInt(monthStr, 10) - 1;

    if (mIndex >= 0 && mIndex < 12) {
      const label = `${monthNames[mIndex]} ${yearStr}`;
      setMonthYear(label);
    } else {
      setMonthYear(value);
    }
  };

  const fetchMonthlyReport = async () => {
    if (!monthInput) {
      setError('Please select a month first.');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const response = await globalApi.get(
        `/reports/monthly-overview?month=${encodeURIComponent(monthInput)}`
      );

      if (response.status === 429) {
        setError('Rate limited - please wait before refreshing');
        setIsLoading(false);
        return;
      }

      const data = await response.json();

      if (data.success && data.data) {
        const overview = data.data;

        const rows = (overview.members || [])
          .filter((m) => m.saccoStatus !== 'Left')
          .map((m) => ({
            memberId: m.memberId,
            memberName: m.fullName,
            totalDeduction: m.monthData?.totalDeductions || 0,
          }));

        setMonthlyReport({
          monthYear: monthYear || overview.month,
          rows,
        });
      } else {
        setError(data.message || 'Failed to load monthly report');
      }
    } catch (err) {
      if (err.message === 'Session expired') {
        return;
      }
      console.error('Error:', err);
      setError('Failed to fetch monthly report');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLoansReport = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await globalApi.get('/reports/loans');

      if (response.status === 429) {
        setError('Rate limited - please wait before refreshing');
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setLoansReport(data.data);
        setLoansPage(1);
      } else {
        setError(data.message || 'Failed to load loans report');
      }
    } catch (err) {
      if (err.message === 'Session expired') {
        return;
      }
      console.error('Error:', err);
      setError('Failed to fetch loans report');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSharesReport = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await globalApi.get('/reports/shares');

      if (response.status === 429) {
        setError('Rate limited - please wait before refreshing');
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSharesReport(data.data);
      } else {
        setError(data.message || 'Failed to load shares report');
      }
    } catch (err) {
      if (err.message === 'Session expired') {
        return;
      }
      console.error('Error:', err);
      setError('Failed to fetch shares report');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExpensesReport = async () => {
    setIsLoading(true);
    setError('');
    try {
      let endpoint = '/reports/expenses';
      
      // Build query params based on filter mode
      const params = new URLSearchParams();
      if (expenseFilterMode === 'month' && expenseMonthInput) {
        params.append('month', expenseMonthInput);
      } else if (expenseFilterMode === 'year' && expenseYearInput) {
        params.append('year', expenseYearInput);
        params.append('month', 'all');
      } else if (expenseFilterMode === 'all') {
        params.append('month', 'all');
      }

      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }

      const response = await globalApi.get(endpoint);

      if (response.status === 429) {
        setError('Rate limited - please wait before refreshing');
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setExpensesReport(data.data);
      } else {
        setError(data.message || 'Failed to load expenses report');
      }
    } catch (err) {
      if (err.message === 'Session expired') {
        return;
      }
      console.error('Error:', err);
      setError('Failed to fetch expenses report');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'loans') {
      fetchLoansReport();
    } else if (activeTab === 'shares') {
      fetchSharesReport();
    } else if (activeTab === 'expenses') {
      fetchExpensesReport();
    }
  }, [activeTab]);

  const resetExpenseForm = () => {
    const today = new Date().toISOString().slice(0, 10);
    setExpenseForm({
      id: '',
      date: today,
      category: 'Uncategorized',
      description: '',
      amount: '',
      paymentMethod: 'Bank',
      paymentDetails: '',
      paidTo: '',
      reference: '',
      notes: '',
    });
  };

  const startEditExpense = (e) => {
    // Warning that edit will be logged
    const ok = window.confirm(
      'EDIT WARNING:\n\nThis edit will be logged permanently.\nThe old value and your user will be recorded in the Expense History.\n\nContinue to edit?'
    );
    if (!ok) return;

    setExpenseForm({
      id: e.id,
      date: e.date || '',
      category: e.category || 'Uncategorized',
      description: e.description || '',
      amount: String(e.amount ?? ''),
      paymentMethod: e.paymentMethod || 'Bank',
      paymentDetails: e.paymentDetails || '',
      paidTo: e.paidTo || '',
      reference: e.reference || '',
      notes: e.notes || '',
    });
  };

  const validateExpenseForm = () => {
    if (!expenseForm.date) return 'Date is required.';
    if (!expenseForm.description.trim()) return 'Description is required.';
    if (!expenseForm.paidTo.trim()) return 'Paid To (who?) is required.';
    if (!expenseForm.amount) return 'Amount is required.';

    const amt = Number(expenseForm.amount);
    if (!Number.isFinite(amt) || amt <= 0) return 'Amount must be greater than 0.';

    if (needsPaymentDetails(expenseForm.paymentMethod) && !expenseForm.paymentDetails.trim()) {
      return `Payment Details is required for ${expenseForm.paymentMethod}.`;
    }
    return '';
  };

  const saveExpense = async () => {
    const msg = validateExpenseForm();
    if (msg) {
      setError(msg);
      return;
    }

    setIsSavingExpense(true);
    setError('');
    try {
      const isEdit = Boolean(expenseForm.id);

      const endpoint = isEdit
        ? `/reports/expenses/${encodeURIComponent(expenseForm.id)}`
        : `/reports/expenses`;

      const payload = {
        date: expenseForm.date,
        category: expenseForm.category,
        description: expenseForm.description,
        amount: Number(expenseForm.amount),
        paymentMethod: expenseForm.paymentMethod,
        paymentDetails: expenseForm.paymentDetails,
        paidTo: expenseForm.paidTo,
        reference: expenseForm.reference,
        notes: expenseForm.notes,
      };

      const response = isEdit
        ? await globalApi.put(endpoint, payload)
        : await globalApi.post(endpoint, payload);

      if (response.status === 429) {
        setError('Rate limited - please wait before trying again');
        setIsSavingExpense(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        resetExpenseForm();
        await fetchExpensesReport();
      } else {
        setError(data.message || 'Failed to save expense');
      }
    } catch (err) {
      if (err.message === 'Session expired') {
        return;
      }
      console.error(err);
      setError('Failed to save expense');
    } finally {
      setIsSavingExpense(false);
    }
  };

  const deleteExpense = async (id) => {
    if (!id) return;

    const ok = window.confirm(
      'DELETE WARNING:\n\nThis delete will be logged permanently.\nThe full old value and your user will be recorded in the Expense History as DELETED.\n\nContinue to delete?'
    );
    if (!ok) return;

    setIsSavingExpense(true);
    setError('');
    try {
      const response = await globalApi.delete(`/reports/expenses/${encodeURIComponent(id)}`);

      if (response.status === 429) {
        setError('Rate limited - please wait before trying again');
        setIsSavingExpense(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        if (expenseForm.id === id) resetExpenseForm();
        await fetchExpensesReport();
      } else {
        setError(data.message || 'Failed to delete expense');
      }
    } catch (err) {
      if (err.message === 'Session expired') {
        return;
      }
      console.error('Error:', err);
      setError('Failed to delete expense');
    } finally {
      setIsSavingExpense(false);
    }
  };

  const openHistory = async (expenseId) => {
    if (!expenseId) return;
    setHistoryModalOpen(true);
    setHistoryExpenseId(expenseId);
    setHistoryLoading(true);
    setHistoryLogs([]);
    setError('');
    try {
      const response = await globalApi.get(`/reports/expenses/${encodeURIComponent(expenseId)}/history`);

      if (response.status === 429) {
        setError('Rate limited - please wait before trying again');
        setHistoryLoading(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setHistoryLogs(data.data || []);
      } else {
        setError(data.message || 'Failed to load history');
      }
    } catch (err) {
      if (err.message === 'Session expired') {
        return;
      }
      console.error('Error:', err);
      setError('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!monthInput) return;
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const url = `${API_URL}/reports/monthly-processing/export?month=${encodeURIComponent(monthInput)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        setError(errData.message || 'Excel export failed');
        return;
      }
      const blob = await response.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `HopeSACCO_MonthlyDeductions_${monthInput}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(dlUrl);
    } catch (err) {
      if (err.message === 'Session expired') return;
      setError('Failed to export Excel file');
    }
  };

  return (
    <AdminLayout activePage="reports" pageTitle="Reports" breadcrumbs={[{ label: 'Reports' }]}>
          <div className="reports-page">
      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <div className="card">
        <div className="card-header">
          <div className="tab-buttons">
            <button
              className={`btn ${activeTab === 'monthly' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveTab('monthly')}
            >
              📅 Monthly / Bursary
            </button>
            <button
              className={`btn ${activeTab === 'loans' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveTab('loans')}
            >
              💳 Loans Report
            </button>
            <button
              className={`btn ${activeTab === 'shares' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveTab('shares')}
            >
              💰 Shares Report
            </button>
            <button
              className={`btn ${activeTab === 'financial-state' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveTab('financial-state')}
            >
              📊 Financial State
            </button>
            <button
              className={`btn ${activeTab === 'expenses' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveTab('expenses')}
            >
              🧾 Expenses
            </button>
            <button
              className={`btn ${activeTab === 'mobile-loan' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveTab('mobile-loan')}
            >
              📲 Mobile Loan
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* MONTHLY TAB */}
          {activeTab === 'monthly' && (
            <MonthlyBursaryTab
              monthInput={monthInput}
              handleMonthChange={handleMonthChange}
              fetchMonthlyReport={fetchMonthlyReport}
              isLoading={isLoading}
              monthlyReport={monthlyReport}
              exportExcel={exportExcel}
            />
          )}

          {/* LOANS TAB */}
          {activeTab === 'loans' && (
            <LoansReportTab
              isLoading={isLoading}
              loansReport={loansReport}
              loansPage={loansPage}
              setLoansPage={setLoansPage}
              loansPageSize={loansPageSize}
              setLoansPageSize={setLoansPageSize}
            />
          )}

          {/* SHARES TAB */}
          {activeTab === 'shares' && (
            <SharesReportTab
              isLoading={isLoading}
              sharesReport={sharesReport}
              sharesSearch={sharesSearch}
              setSharesSearch={setSharesSearch}
            />
          )}

          {/* FINANCIAL STATE TAB */}
          {activeTab === 'financial-state' && (
            <SaccoFinancialStatePage />
          )}

          {/* EXPENSES TAB */}
          {activeTab === 'expenses' && (
            <ExpensesTab
              expenseFilterMode={expenseFilterMode}
              setExpenseFilterMode={setExpenseFilterMode}
              expenseMonthInput={expenseMonthInput}
              setExpenseMonthInput={setExpenseMonthInput}
              expenseYearInput={expenseYearInput}
              setExpenseYearInput={setExpenseYearInput}
              expensesReport={expensesReport}
              setExpensesReport={setExpensesReport}
              fetchExpensesReport={fetchExpensesReport}
              isLoading={isLoading}
              expenseForm={expenseForm}
              setExpenseForm={setExpenseForm}
              resetExpenseForm={resetExpenseForm}
              isSavingExpense={isSavingExpense}
              saveExpense={saveExpense}
              startEditExpense={startEditExpense}
              deleteExpense={deleteExpense}
              openHistory={openHistory}
              historyModalOpen={historyModalOpen}
              setHistoryModalOpen={setHistoryModalOpen}
              historyLoading={historyLoading}
              historyExpenseId={historyExpenseId}
              setHistoryExpenseId={setHistoryExpenseId}
              historyLogs={historyLogs}
              setHistoryLogs={setHistoryLogs}
            />
          )}

          {/* MOBILE LOAN TAB */}
          {activeTab === 'mobile-loan' && (
            <MobileLoanTab />
          )}
        </div>
      </div>
    </div></AdminLayout>
  );
};

export default ReportsPage;
